import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Search } from '@mui/icons-material';
import { Button, InputAdornment, Paper, TextField } from '@mui/material';

import EmptyCard from '@components/EmptyCard/EmptyCard';
import Loadable from '@components/Loadable/Loadable';
import {
  handleAdminSearchOrganizations,
  handleGetAdminOrganizationDetails,
} from '@store/features/adminOrganizations/actions';
import {
  adminOrganizationActiveIdSelector,
  adminOrganizationCreatingNewSelector,
  adminOrganizationDetailsErrorSelector,
  adminOrganizationDetailsIsLoadingSelector,
  adminOrganizationDetailsSelector,
  adminOrganizationsDataSelector,
  adminOrganizationsErrorSelector,
  adminOrganizationsIsLoadingSelector,
} from '@store/features/adminOrganizations/selectors';
import {
  removeAdminOrganizationDetails,
  removeAdminOrganizationList,
  setAdminOrganizationCreatingNew,
} from '@store/features/adminOrganizations/slice';
import { searchAdminOrganizationsPayload } from '@store/features/adminOrganizations/utils';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';

import NewOrganization from './NewOrganization/NewOrganization';
import OrganizationDetails from './OrganizationDetails/OrganizationDetails';
import OrganizationList from './OrganizationList/OrganizationList';

import './style.scss';

const Organizations: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('adminOrganizations');

  const data = useSelector(adminOrganizationsDataSelector);
  const isLoading = useSelector(adminOrganizationsIsLoadingSelector);
  const error = useSelector(adminOrganizationsErrorSelector);
  const organizationId = useSelector(adminOrganizationActiveIdSelector);
  const organization = useSelector(adminOrganizationDetailsSelector);
  const organizationIsLoading = useSelector(adminOrganizationDetailsIsLoadingSelector);
  const organizationError = useSelector(adminOrganizationDetailsErrorSelector);
  const isCreatingNew = useSelector(adminOrganizationCreatingNewSelector);
  const [search, setSearch] = useState('');

  const handleFetchOrganizations = useCallback(() => {
    dispatch(handleAdminSearchOrganizations(searchAdminOrganizationsPayload));
  }, [dispatch]);

  const handleSearchOrganizations = useCallback(() => {
    dispatch(handleAdminSearchOrganizations({ ...searchAdminOrganizationsPayload, search }));
  }, [dispatch, search]);

  const handleFetchOrganizationDetails = useCallback(
    (organizationId: number) => {
      dispatch(handleGetAdminOrganizationDetails(organizationId));
    },
    [dispatch],
  );

  const handleRetryFetchOrganizationDetails = useCallback(() => {
    if (organizationId) handleFetchOrganizationDetails(organizationId);
  }, [organizationId, handleFetchOrganizationDetails]);

  const handleCreateNew = useCallback(() => {
    dispatch(setAdminOrganizationCreatingNew());
  }, [dispatch]);

  useEffect(() => {
    handleFetchOrganizations();
  }, [handleFetchOrganizations]);

  useEffect(() => {
    eventBus.on([EventType.ADMIN_CREATE_NEW_ORGANIZATION_SUCCESS], () => {
      dispatch(removeAdminOrganizationDetails());
      handleFetchOrganizations();
    });
    eventBus.on([EventType.ADMIN_EDIT_ORGANIZATION_SUCCESS], () => {
      handleFetchOrganizations();
    });

    return () =>
      eventBus.off([
        EventType.ADMIN_CREATE_NEW_ORGANIZATION_SUCCESS,
        EventType.ADMIN_EDIT_ORGANIZATION_SUCCESS,
      ]);
  }, [eventBus, dispatch, handleFetchOrganizations]);

  useEffect(
    () => () => {
      dispatch(removeAdminOrganizationList());
      dispatch(removeAdminOrganizationDetails());
    },
    [dispatch],
  );

  return (
    <div className="organizations">
      <div className="organizations__header">
        <div className="organizations__title">{t('admin.axisOrganizations')}</div>

        <TextField
          className="organizations__search"
          placeholder={t('admin.searchOrganizations')}
          variant="outlined"
          size="small"
          value={search}
          onKeyDown={event => event.key === 'Enter' && handleSearchOrganizations()}
          onChange={event => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="inherit" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button onClick={handleSearchOrganizations} variant="text">
                  {t('admin.search')}
                </Button>
              </InputAdornment>
            ),
          }}
        />

        <Button onClick={handleCreateNew} variant="contained" className="organizations__create-new">
          {t('admin.addNewOrganization')}
        </Button>
      </div>

      <div className="organizations__body">
        <Paper className="organizations__list" elevation={0}>
          <Loadable
            isLoading={isLoading}
            error={error && t('errors.organizationList')}
            onRetry={handleFetchOrganizations}
          >
            {data.length ? (
              <OrganizationList
                data={data}
                activeId={organizationId}
                onClick={handleFetchOrganizationDetails}
              />
            ) : (
              <EmptyCard
                className="organizations__list-placeholder"
                borderType="none"
                description={t('common.noResultsFound')}
              />
            )}
          </Loadable>
        </Paper>

        <Paper className="organizations__details" elevation={0}>
          {isCreatingNew ? (
            <NewOrganization />
          ) : organizationId ? (
            <Loadable
              isLoading={organizationIsLoading}
              error={organizationError && t('errors.organizationDetails')}
              onRetry={handleRetryFetchOrganizationDetails}
            >
              {organization && <OrganizationDetails data={organization} />}
            </Loadable>
          ) : (
            <EmptyCard
              className="organizations__details-placeholder"
              borderType="none"
              icon="/images/img-clipboard.svg"
              description={t('admin.organizationDetailsPlaceholder')}
            />
          )}
        </Paper>
      </div>
    </div>
  );
};

export default Organizations;
